/**
 * The one pool-card definition every other web pools screen reuses.
 * `statusLabel` is rendered verbatim per decision C18 — this component never
 * parses it.
 * A card whose model is not selectable (an archived pool, a suspended
 * membership) renders as a static group, not a button, because there is no
 * pool screen to open for it (ISSUE-1037). A pending request is the one
 * exception: its card opens the pool's preview with Cancel request, v1's Pool
 * Info sheet (ISSUE-1341).
 */
import { Card, cn, FOCUS_RING } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";
import { PendingPoolDialog } from "./PendingPoolDialog";
import { PoolCardMenu } from "./PoolCardMenu";
import { PoolCardShare } from "./PoolCardShare";
import { PoolCardContent } from "./PoolCardContent";

import type {
  typePoolCardModel,
  typePoolMarkTone,
  typePoolStatusTone,
} from "./poolsListState";

export const MARK_TONE_CLASS: Record<typePoolMarkTone, string> = {
  primary: "bg-primary text-ink-inverse",
  accent: "bg-accent text-ink-inverse",
  success: "bg-success text-ink-inverse",
  warning: "bg-warning text-ink-inverse",
  info: "bg-info text-ink-inverse",
  neutral: "bg-ink-secondary text-ink-inverse",
};

const STATUS_TONE_CLASS: Record<typePoolStatusTone, string> = {
  ok: "text-success",
  warning: "text-warning",
  neutral: "text-ink-secondary",
};

export type typePoolCardProps = {
  model: typePoolCardModel;
  isSelected: boolean;
  onSelect: (poolId: string) => void;
};

export const PoolCardBody = ({
  model,
  children,
}: {
  model: typePoolCardModel;
  children?: ReactNode;
}): ReactNode => (
  <PoolCardContent
    name={model.name}
    contestType={model.contestType ?? model.contestLabel}
    sport={model.sportKey}
    imageUrl={model.bannerImageUrl}
    byline={model.creatorHandle ? `@${model.creatorHandle}` : undefined}
    date={model.seasonLabel}
    facts={[model.leagueLabel, model.contestLabel]}
  >
    <span
      className={cn("text-caption", STATUS_TONE_CLASS[model.statusTone])}
      data-testid={`account-pool-status-${model.poolId}`}
    >
      {model.statusLabel}
    </span>
    {children}
  </PoolCardContent>
);

const PendingPoolCard = ({
  model,
}: {
  model: typePoolCardModel;
}): ReactNode => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Card
        aria-label={`${model.name}, request pending`}
        data-testid={`pool-card-${model.poolId}`}
        padding="default"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <PoolCardBody model={model} />
      </Card>
      <PendingPoolDialog
        open={isOpen}
        poolId={model.poolId}
        poolName={model.name}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </>
  );
};

export const PoolCard = ({
  model,
  isSelected,
  onSelect,
}: typePoolCardProps): ReactNode => {
  if (model.isPendingRequest) {
    return <PendingPoolCard model={model} />;
  }
  if (model.isSelectable && model.adminRole) {
    return (
      <Card
        padding="default"
        className="relative"
        data-testid={`pool-card-${model.poolId}`}
        tone={isSelected ? "tinted" : "default"}
      >
        <button
          type="button"
          aria-label={model.name}
          aria-current={isSelected ? "true" : undefined}
          className={cn("pool-card-open", FOCUS_RING)}
          onClick={() => {
            onSelect(model.poolId);
          }}
        />
        <div className="pool-card-admin-content">
          <PoolCardBody model={model}>
            <div
              className="pool-card-share"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <PoolCardShare poolId={model.poolId} poolName={model.name} />
              <PoolCardMenu
                poolId={model.poolId}
                poolName={model.name}
                canLeave={model.adminRole !== "owner"}
                isArchivedByMe={model.isArchivedByMe}
              />
            </div>
          </PoolCardBody>
        </div>
      </Card>
    );
  }
  // ISSUE-1344. The card itself is a <button>, and a button cannot hold
  // another, so More options sits beside it in the same corner an admin's
  // share button uses, layered above the card rather than inside it.
  return model.isSelectable ? (
    <div className="relative">
      <Card
        aria-label={model.name}
        aria-current={isSelected ? "true" : undefined}
        data-testid={`pool-card-${model.poolId}`}
        tone={isSelected ? "tinted" : "default"}
        padding="default"
        onClick={() => {
          onSelect(model.poolId);
        }}
      >
        <PoolCardBody model={model} />
      </Card>
      <div className="absolute right-2 bottom-2">
        <PoolCardMenu
          poolId={model.poolId}
          poolName={model.name}
          canLeave
          isArchivedByMe={model.isArchivedByMe}
        />
      </div>
    </div>
  ) : (
    // ISSUE-1343. A closed pool or a suspended membership cannot be opened,
    // but the fan can still archive it out of their list.
    <div className="relative">
      <Card
        role="group"
        aria-label={model.name}
        aria-current={isSelected ? "true" : undefined}
        data-testid={`pool-card-${model.poolId}`}
        tone={isSelected ? "tinted" : "default"}
        padding="default"
      >
        <PoolCardBody model={model} />
      </Card>
      <div className="absolute right-2 bottom-2">
        <PoolCardMenu
          poolId={model.poolId}
          poolName={model.name}
          canLeave={false}
          isArchivedByMe={model.isArchivedByMe}
          rows="archiveOnly"
        />
      </div>
    </div>
  );
};
